import React, { useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';

interface ConfirmBankDetailsModalProps {
  show: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
  bankDetails: {
    nameAsOnBankAccount: string;
    bankAccount: string;
    ifsc: string;
    bankName: string;
  };
}

const checkItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  padding: '8px 0',
  cursor: 'pointer',
  userSelect: 'none',
};

const checkBoxBase: React.CSSProperties = {
  width: 18,
  height: 18,
  borderRadius: 4,
  border: '1.5px solid #CED4DA',
  backgroundColor: '#fff',
  flexShrink: 0,
  marginTop: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.15s ease',
};

const checkBoxChecked: React.CSSProperties = {
  ...checkBoxBase,
  backgroundColor: '#5a9140ff',
  borderColor: '#5a9140ff',
};

const CheckIcon = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#fff"
    strokeWidth="3.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12l5 5 9-9" />
  </svg>
);

const ConfirmBankDetailsModal: React.FC<ConfirmBankDetailsModalProps> = ({
  show,
  onClose,
  onConfirm,
  isSubmitting,
  bankDetails,
}) => {
  const [ownAccountChecked, setOwnAccountChecked] = useState(false);
  const [notBusinessChecked, setNotBusinessChecked] = useState(false);
  const [finalConsentChecked, setFinalConsentChecked] = useState(false);

  const allChecked = ownAccountChecked && notBusinessChecked && finalConsentChecked;

  const handleClose = () => {
    setOwnAccountChecked(false);
    setNotBusinessChecked(false);
    setFinalConsentChecked(false);
    onClose();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop="static"
      keyboard={false}
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title className="fw-semibold">
          Review &amp; Confirm Bank Details
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>

        {/* ── Review Card ── */}
        <div className="border rounded-3 p-3 mb-3 bg-light">
          <h6 className="fw-semibold mb-2 text-muted">Entered Bank Information</h6>
          <div className="row gy-2">
            <div className="col-md-6">
              <div className="small text-muted">Account Holder Name</div>
              <div className="fw-semibold">{bankDetails.nameAsOnBankAccount}</div>
            </div>
            <div className="col-md-6">
              <div className="small text-muted">Account Number</div>
              <div className="fw-semibold">{bankDetails.bankAccount}</div>
            </div>
            <div className="col-md-6">
              <div className="small text-muted">IFSC Code</div>
              <div className="fw-semibold">{bankDetails.ifsc}</div>
            </div>
            <div className="col-md-6">
              <div className="small text-muted">Bank Name</div>
              <div className="fw-semibold">{bankDetails.bankName}</div>
            </div>
          </div>
        </div>

        {/* ── Quick Checks ── */}
        <div
          className="border rounded-3 p-3 mb-3"
          style={{ backgroundColor: '#FFFDF5', borderColor: '#E8D99A' }}
        >
          <div
            className="d-flex align-items-center gap-2 mb-2"
            style={{ borderBottom: '1px solid #F0E6A8', paddingBottom: 8 }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#99834D" strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4" />
              <circle cx="12" cy="16" r="1" fill="#99834D" />
            </svg>
            <h6 className="fw-semibold mb-0" style={{ fontSize: 13, color: '#6B5A2E' }}>
              Quick Checks — confirm before you submit
            </h6>
          </div>

          {/* ── Plain info items ── */}
          <ul
            className="mb-2 ps-3 small"
            style={{ color: '#6B5A2E', lineHeight: 1.6 }}
          >
            <li>
              Carefully review the <strong>bank details</strong> once before <strong>submitting</strong>
            </li>
            <li>
              Use the <strong>IFSC</strong> from your bank records (<strong>Passbook</strong> or <strong>Cheque</strong>)
            </li>
          </ul>

          {/* ── Divider ── */}
          <div style={{ borderTop: '1px dashed #E8D99A', marginBottom: 8 }} />

          {/* ── Mandatory checkbox 1 ── */}
          <div
            style={{
              ...checkItemStyle,
              borderBottom: '1px solid #F0E6A8',
              paddingBottom: 8,
              marginBottom: 2,
            }}
            onClick={() => !isSubmitting && setOwnAccountChecked(v => !v)}
          >
            <div style={ownAccountChecked ? checkBoxChecked : checkBoxBase}>
              {ownAccountChecked && <CheckIcon />}
            </div>
            <div style={{ fontSize: 13, color: '#4A3B1A', lineHeight: 1.45 }}>
              The details entered are from my own{' '}
              <strong>active savings bank account</strong>
              <div style={{ fontSize: 11, color: '#99834D', marginTop: 1, fontWeight: 500 }}>
                Not spouse, parent, child, or friend's account
              </div>
            </div>
          </div>

          {/* ── Mandatory checkbox 2 ── */}
          <div
            style={checkItemStyle}
            onClick={() => !isSubmitting && setNotBusinessChecked(v => !v)}
          >
            <div style={notBusinessChecked ? checkBoxChecked : checkBoxBase}>
              {notBusinessChecked && <CheckIcon />}
            </div>
            <div style={{ fontSize: 13, color: '#4A3B1A', lineHeight: 1.45 }}>
              This is <strong>NOT</strong> a current, business, or enterprise account
              <div style={{ fontSize: 11, color: '#99834D', marginTop: 1, fontWeight: 500 }}>
                eBullion only accepts personal savings accounts
              </div>
            </div>
          </div>

          {/* ── Progress indicator ── */}
          {(!ownAccountChecked || !notBusinessChecked) && (
            <div
              style={{
                marginTop: 8,
                padding: '6px 10px',
                background: '#FFF3CD',
                borderRadius: 6,
                fontSize: 11,
                color: '#856404',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#856404" strokeWidth="2" strokeLinecap="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                <path d="M12 9v4" /><circle cx="12" cy="17" r="1" fill="#856404" />
              </svg>
              Please confirm{' '}
              {!ownAccountChecked && !notBusinessChecked
                ? 'both items'
                : 'the remaining item'}{' '}
              above to proceed
            </div>
          )}

          {(ownAccountChecked && notBusinessChecked) && (
            <div
              style={{
                marginTop: 8,
                padding: '6px 10px',
                background: '#D4EDDA',
                borderRadius: 6,
                fontSize: 11,
                color: '#155724',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#155724" strokeWidth="2.5" strokeLinecap="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M8 12.5l2.5 2.5L16 9.5" />
              </svg>
              Both checks confirmed — you may proceed
            </div>
          )}
        </div>

        {/* ── Common Mistakes ── */}
        <div className="border rounded-3 p-3 mb-3">
          <h6 className="fw-semibold mb-2 text-danger" style={{ fontSize: 13 }}>
            Common mistakes to avoid
          </h6>
          <ul className="mb-0 ps-3 small text-muted" style={{ lineHeight: 1.6 }}>
            <li>Entering <strong>extra or fewer 0's</strong> in the account number</li>
            <li>Entering an account number with <strong>incorrect length</strong></li>
            <li>Adding <strong>someone else's bank account</strong> by mistake</li>
            <li>Using an <strong>inactive</strong> or <strong>closed bank account</strong></li>
          </ul>
        </div>

        {/* ── Final consent ── */}
        <div
          className="border rounded-3 p-3"
          style={{
            backgroundColor: finalConsentChecked ? '#F0FFF4' : '#fff',
            borderColor: finalConsentChecked ? '#C3E6CB' : '#CED4DA',
            transition: 'background-color 0.2s, border-color 0.2s',
          }}
        >
          <div
            style={checkItemStyle}
            onClick={() => !isSubmitting && setFinalConsentChecked(v => !v)}
          >
            <div style={finalConsentChecked ? checkBoxChecked : checkBoxBase}>
              {finalConsentChecked && <CheckIcon />}
            </div>
            <label
              style={{
                fontSize: 13,
                cursor: 'pointer',
                color: finalConsentChecked ? '#155724' : '#495057',
                fontWeight: 500,
                lineHeight: 1.45,
              }}
            >
              I confirm that the bank details provided above are correct and
              belong to me. I authorize verification of these details.
            </label>
          </div>
        </div>

      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose} disabled={isSubmitting}>
          Edit Details
        </Button>

        <Button
          variant="primary"
          onClick={onConfirm}
          disabled={isSubmitting || !allChecked}
          style={{
            backgroundColor: allChecked ? '#99834D' : undefined,
            borderColor: allChecked ? '#99834D' : undefined,
          }}
        >
          {isSubmitting ? (
            <>
              <Spinner size="sm" className="me-2" />
              Verifying...
            </>
          ) : (
            'Confirm & Submit'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmBankDetailsModal;